import { currencyFormat } from "../util";
import { HerdScenario } from "./cow-model-parameters.model";
import { Group } from "./group.model";
import { ValueItemView } from "./value-item-view.model";

export const CalfAgeDays = 200;
export const CalfBirthWeightKg = 40;

export function isHerdValueModel(o: unknown): o is HerdValueModel {
  return "cowMortality" in (o as HerdValueModel);
}

export interface HerdValueModel extends Required<Group> {
  cowMortality: number;
  cowMortalityBeforeCalving: number;
  cowsAliveAtCalving: number;
  cowsPregnant: number;
  cowsPregnantWithTwins: number;
  calvesAliveAtCalving: number;
  cowAbortions: number;
  calfAbortions: number;
  calvesRemainingAfterAbortions: number;
  stillborns: number;
  firstWeekCalfLosses: number;
  calfLossesAfterFirstWeek: number;
  calvesAliveAtWeaning: number;
  cowMortalityCalvingToWeaning: number;
  cowsAliveAtWeaning: number;
  calfWeightPerHead: number;
  calfWeightTotal: number;
  calvesValue: number;
  cowsValues: number;
  breedingHerdValue: number;
  juvenileCropValue: number;
  herdValue: number;
  valuePerHead: number;
  valueGain: number;
  totalCalfLosses: number;
}

// TODO: consider refactoring or renaming this; it's not a model
export const getHerdValue = (
  herd: Required<Group>,
  scenario: HerdScenario
): HerdValueModel => {
  // B13 = $B8*B12
  const cowMortality =
    herd.breedingMotherInitialHeadCount * scenario.cowMortalityFraction;

  // B17 = $B8*(B12*0.33333)
  const cowMortalityBeforeCalving = cowMortality / 3;

  // B18 = $B8-B17
  const cowsAliveAtCalving =
    herd.breedingMotherInitialHeadCount - cowMortalityBeforeCalving;

  const cowsPregnant = cowsAliveAtCalving * scenario.cowsPregnantFraction;

  const cowsPregnantWithTwins = cowsPregnant * scenario.cowsWithTwinsFraction;

  // B19 = (B18*B15)+(B18*B15*B16)
  const calvesAliveAtCalving =
    cowsPregnant * (1 + scenario.cowsWithTwinsFraction);

  // B25 = B21*B18 (spreadsheet is incorrect, but this has no consequence for bottom line)
  const cowAbortions = cowsPregnant * scenario.abortionFraction;

  // B26 = (B19*B21)+(B19*B21*B16)
  const calfAbortions = calvesAliveAtCalving * scenario.abortionFraction;

  const calvesRemainingAfterAbortions = calvesAliveAtCalving - calfAbortions;

  // B27 = (B19-B26)*B22
  const stillborns =
    calvesRemainingAfterAbortions * scenario.stillbirthFraction;

  // B28 = (B19-B26-B27)*B23
  const firstWeekCalfLosses =
    (calvesRemainingAfterAbortions - stillborns) *
    scenario.firstWeekLossesFraction;

  // B29 = (B19-B26-B27-B28)*B24
  const calfLossesAfterFirstWeek =
    (calvesRemainingAfterAbortions - stillborns - firstWeekCalfLosses) *
    scenario.lossesAfterFirstWeekFraction;

  // B30 = B19-B26-B27-B28-B29
  const calvesAliveAtWeaning =
    calvesRemainingAfterAbortions -
    stillborns -
    firstWeekCalfLosses -
    calfLossesAfterFirstWeek;

  // B31 = $B8*(B12*0.66666)
  const cowMortalityCalvingToWeaning =
    (herd.breedingMotherInitialHeadCount * scenario.cowMortalityFraction * 2) /
    3;

  // B32 = $B8-B17-B31
  const cowsAliveAtWeaning =
    herd.breedingMotherInitialHeadCount -
    cowMortalityBeforeCalving -
    cowMortalityCalvingToWeaning;

  // B35 = 40+(B34*200)
  const calfWeightPerHead =
    CalfBirthWeightKg + scenario.calfGrowthRateKgPerDay * CalfAgeDays;

  // B36 = B35*B30
  const calfWeightTotal = calfWeightPerHead * calvesAliveAtWeaning;

  // B37 = B36*$B6
  const calvesValue = calfWeightTotal * herd.liveWeightJuvenileValuePerKg;

  // B38 = B32*$B7
  const cowsValues = cowsAliveAtWeaning * herd.breedingMotherValuePerHead;

  // B2 = B39 = B37 + B38
  const herdValue = calvesValue + cowsValues;

  // B3 = B2/$B8
  const valuePerHead = herdValue / herd.breedingMotherInitialHeadCount;

  const valueGain =
    herdValue -
    herd.breedingMotherInitialHeadCount * herd.breedingMotherValuePerHead;

  const totalCalfLosses =
    calfAbortions + stillborns + firstWeekCalfLosses + calfLossesAfterFirstWeek;

  return {
    cowMortality,
    cowMortalityBeforeCalving,
    cowsAliveAtCalving,
    cowsPregnant,
    cowsPregnantWithTwins,
    calvesAliveAtCalving,
    cowAbortions,
    calfAbortions,
    calvesRemainingAfterAbortions,
    stillborns,
    firstWeekCalfLosses,
    calfLossesAfterFirstWeek,
    calvesAliveAtWeaning,
    cowMortalityCalvingToWeaning,
    cowsAliveAtWeaning,
    calfWeightPerHead,
    calfWeightTotal,
    calvesValue,
    cowsValues,
    breedingHerdValue: cowsValues,
    juvenileCropValue: calvesValue,
    herdValue,
    valuePerHead,
    valueGain,
    totalCalfLosses,
    ...herd,
  };
};

type HerdValueViewType = {
  [key in keyof HerdValueModel]?: ValueItemView;
};

export const HerdValueView: HerdValueViewType = {
  breedingMotherInitialHeadCount: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Cows to the bull",
  },
  cowsAliveAtCalving: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Cows alive at calving",
    toolTip:
      "Note that in this tool one third of annual cow mortality is assumed to occur before calving",
  },
  calvesAliveAtCalving: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Calves in utero",
    toolTip: "Total of number of calves scanned",
  },
  calvesAliveAtWeaning: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Calves alive at weaning",
    toolTip:
      "Note that the aborted calves, stillbirths and live calf losses were taken into account",
  },
  cowsAliveAtWeaning: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Cows alive at weaning",
    toolTip: "Cows to the bull minus annual cow mortality",
  },
  calfWeightPerHead: {
    includedInVerboseView: true,
    format: ({ value }) => `${value.toFixed(1)} kg`,
    description: "Calf weight at weaning",
    toolTip: `Calf weight per head at ${CalfAgeDays} days, assuming a birth weight of ${CalfBirthWeightKg} kg`,
  },
  breedingHerdValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value of breeding herd at weaning",
    toolTip: "Value of breeding females at weaning",
  },
  juvenileCropValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value of calves at weaning",
  },
  herdValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Total herd value at weaning",
    emphasized: true,
    toolTip:
      "Total of value breeding females at weaning and value of calves at weaning",
  },
  valuePerHead: {
    includedInVerboseView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value per head",
    toolTip:
      "Total herd value at weaning divided by the number of females to the bull",
  },
  valueGain: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value gain",
    toolTip: "The overall value gain of the herd at weaning",
    emphasized: true,
  },
};
